import { HttpClient } from '@angular/common/http';
import { Observable, shareReplay, tap } from 'rxjs';
import { Injectable } from '@angular/core';
import { DataProvider } from 'pcs-domain';
import { External } from '../decorators/app.decorators';
import { EnvironmentService } from './environment.service';
import { AppConstants } from '../data-models/app-constants';
import {
  GetComponents,
  GetWorkloadInputConfigiuration,
  GetDhciInputConfiguration,
  GetHftRaidsInputConfigiuration,
  GetIOProfileInputConfigiuration,
  GetvSANOverheadsInputConfigiuration,
  GetWorkloadTypeConfiguration,
} from '../../store/project-details/project-details.actions';

@External()
@Injectable({
  providedIn: 'root'
})
export class DataProviderConfigService {
  config: any;

  constructor(private envService: EnvironmentService, private httpClient: HttpClient) {
    this.config = this.envService.config;
  }

  getComponents(action: GetComponents) {
    return this.httpClient.get(this.buildComponentsUrl(action));
  }

  getWorkloadInputConfiguration(action: GetWorkloadInputConfigiuration) {
    return this.httpClient.get(this.buildWorkloadInputConfigurationUrl(action));
  }

  /**
   * dHCI limits/defaults are constant for a given product + version (the query params below are
   * fixed by design), so the in-flight/completed request is shared and reused for every later call
   * instead of hitting the API on each HCI -> dHCI toggle. A failed request is evicted so the next
   * call retries.
   */
  private readonly dhciInputConfigurationCache = new Map<string, Observable<DataProvider.InputConfigurationResponse>>();

  getDhciInputConfiguration(action: GetDhciInputConfiguration): Observable<DataProvider.InputConfigurationResponse> {
    const url = this.buildDhciInputConfigurationUrl(action);
    let request$ = this.dhciInputConfigurationCache.get(url);
    if (!request$) {
      request$ = this.httpClient.get<DataProvider.InputConfigurationResponse>(url).pipe(
        tap({ error: () => this.dhciInputConfigurationCache.delete(url) }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
      this.dhciInputConfigurationCache.set(url, request$);
    }
    return request$;
  }

  getHftRaidsInputConfiguration(action: GetHftRaidsInputConfigiuration) {
    return this.httpClient.get(this.buildHftRaidsInputConfigurationUrl(action));
  }

  getIoProfileInputConfiguration(action: GetIOProfileInputConfigiuration) {
    return this.httpClient.get(this.buildIoProfileInputConfigurationUrl(action));
  }

  getvSANOverheadsInputConfiguration(action: GetvSANOverheadsInputConfigiuration) {
    return this.httpClient.get(this.buildvSANOverheadsInputConfigurationUrl(action));
  }

  getWorkloadTypeConfiguration(action: GetWorkloadTypeConfiguration) {
    return this.httpClient.get(this.buildWorkloadTypeConfigurationUrl(action));
  }

  private buildComponentsUrl(action: GetComponents): string {
    return this.config?.dataProviderAPIUrl + 'products/' + action.productName + '/versions/' + action.version + '/components' + '?domain=' + action.domain + '&architecture=' + action.architecture;
  }

  private buildWorkloadInputConfigurationUrl(action: GetWorkloadInputConfigiuration): string {
    return `${this.config?.dataProviderAPIUrl}products/${action.productName}/versions/${action.version}/configurations?storageType=${action.storageType}&vsanArchitecture=${action.vsanArchitecture}&workloadOption=${action.workloadOption}&deploymentCode=${action.deploymentCode}&profileModeCode=${action.profileModeCode}&domain=${action.domain}`;
  }

  private buildDhciInputConfigurationUrl(action: GetDhciInputConfiguration): string {
    return `${this.config?.dataProviderAPIUrl}products/${action.productName}/versions/${action.version}/configurations?storageType=VSAN&vsanArchitecture=ESA&workloadOption=GPW&deploymentCode=GVM&clusterType=D_HCI`;
  }

  private buildHftRaidsInputConfigurationUrl(action: GetHftRaidsInputConfigiuration): string {
    return `${this.config?.dataProviderAPIUrl}products/${action.productName}/versions/${action.version}/configurations?storageType=${action.storageType}&vsanArchitecture=${action.vsanArchitecture}&workloadOption=${action.workloadOption}&deploymentCode=${action.deploymentCode}&profileModeCode=${action.profileModeCode}&hostfailurestotolerate=${action.hostfailurestotolerate}&strategies=${action.strategies}${action.clusterType ? `&clusterType=${action.clusterType}` : ''}`;
  }

  private buildIoProfileInputConfigurationUrl(action: GetIOProfileInputConfigiuration): string {
    return `${this.config?.dataProviderAPIUrl}products/${action.productName}/versions/${action.version}/configurations?storageType=${action.storageType}&vsanArchitecture=${action.vsanArchitecture}&workloadOption=${action.workloadOption}&deploymentCode=${action.deploymentCode}&profileModeCode=${action.profileModeCode}&ioaccesspattern=${action.ioaccesspattern}&strategies=${action.strategies}`;
  }

  private hardwareParamsApplicable(hardwareConfigMode?: string): boolean {
    return hardwareConfigMode !== 'no-preference' && hardwareConfigMode !== 'use-existing-cluster';
  }

  private buildvSANOverheadsInputConfigurationUrl(action: GetvSANOverheadsInputConfigiuration): string {
    if (action.vsanArchitecture?.toLocaleLowerCase() === AppConstants.vSAN_ESA.toLocaleLowerCase()) {
      let url = `${this.config?.dataProviderAPIUrl}products/${action.productName}/versions/${action.version}/configurations?storageType=${action.storageType}&vsanArchitecture=${action.vsanArchitecture}&workloadOption=${action.workloadOption}&deploymentCode=${action.deploymentCode}&profileModeCode=${action.profileModeCode}&hostfailurestotolerate=${action.hostfailurestotolerate}&raid=${action.raid}&ioaccesspattern=${action.ioaccesspattern}&ioRatio=${action.ioRatio}&ioSize=${action.ioSize}`;
      if (this.hardwareParamsApplicable(action.hardwareConfigMode)) {
        url += `&nodeCapacityTb=${action.nodeCapacityTb}&memoryGb=${action.memoryGb}&networkGbe=${action.networkGbe}&cpuCoresPerNode=${action.cpuCoresPerNode}&maxStorageDevices=${action.maxStorageDevices}`;
      }
      url += `&clusterType=${action.clusterType}&storageEfficiencyMode=${action.storageEfficiencyMode}&storageEfficiencyValue=${action.storageEfficiencyValue}&strategies=${action.strategies}`;
      return url;
    }
    if ((action.profileModeCode)?.toLocaleLowerCase() == ('AF').toLocaleLowerCase()) {
      return `${this.config?.dataProviderAPIUrl}products/${action.productName}/versions/${action.version}/configurations?storageType=${action.storageType}&vsanArchitecture=${action.vsanArchitecture}&workloadOption=${action.workloadOption}&deploymentCode=${action.deploymentCode}&profileModeCode=${action.profileModeCode}&hostfailurestotolerate=${action.hostfailurestotolerate}&raid=${action.raid}&ioaccesspattern=${action.ioaccesspattern}&ioRatio=${action.ioRatio}&ioSize=${action.ioSize}&storageEfficiencyMode=${action.storageEfficiencyMode}&storageEfficiencyValue=${action.storageEfficiencyValue}&strategies=${action.strategies}`;
    }
    return `${this.config?.dataProviderAPIUrl}products/${action.productName}/versions/${action.version}/configurations?storageType=${action.storageType}&vsanArchitecture=${action.vsanArchitecture}&workloadOption=${action.workloadOption}&deploymentCode=${action.deploymentCode}&profileModeCode=${action.profileModeCode}&hostfailurestotolerate=${action.hostfailurestotolerate}&raid=${action.raid}&ioaccesspattern=${action.ioaccesspattern}&ioRatio=${action.ioRatio}&ioSize=${action.ioSize}&strategies=${action.strategies}`;
  }

  private buildWorkloadTypeConfigurationUrl(action: GetWorkloadTypeConfiguration): string {
    let url: string;
    if (action.ioaccesspattern) { //could use any of hostfailurestotolerate, raid, ioratio, iosize, ioaccesspattern to get default workload config.
      if (action.vsanArchitecture?.toLocaleLowerCase() === AppConstants.vSAN_ESA.toLocaleLowerCase()) {
        url = `${this.config?.dataProviderAPIUrl}products/${action.productName}/versions/${action.version}/configurations?storageType=${action.storageType}&vsanArchitecture=${action.vsanArchitecture}&workloadOption=${action.workloadOption}&deploymentCode=${action.deploymentCode}&profileModeCode=${action.profileModeCode}&hostfailurestotolerate=${action.hostfailurestotolerate}&raid=${action.raid}&ioaccesspattern=${action.ioaccesspattern}&ioRatio=${action.ioRatio}&ioSize=${action.ioSize}`;
        if (this.hardwareParamsApplicable(action.hardwareConfigMode)) {
          url += `&nodeCapacityTb=${action.nodeCapacityTb}&memoryGb=${action.memoryGb}&networkGbe=${action.networkGbe}&cpuCoresPerNode=${action.cpuCoresPerNode}&maxStorageDevices=${action.maxStorageDevices}`;
        }
        url += `&clusterType=${action.clusterType}&strategies=${action.strategies}`;
      } else {
        url = `${this.config?.dataProviderAPIUrl}products/${action.productName}/versions/${action.version}/configurations?storageType=${action.storageType}&vsanArchitecture=${action.vsanArchitecture}&workloadOption=${action.workloadOption}&deploymentCode=${action.deploymentCode}&profileModeCode=${action.profileModeCode}&hostfailurestotolerate=${action.hostfailurestotolerate}&raid=${action.raid}&ioaccesspattern=${action.ioaccesspattern}&ioRatio=${action.ioRatio}&ioSize=${action.ioSize}&strategies=${action.strategies}`;
      }
    } else {
      url = `${this.config?.dataProviderAPIUrl}products/${action.productName}/versions/${action.version}/configurations?storageType=${action.storageType}&vsanArchitecture=${action.vsanArchitecture}&workloadOption=${action.workloadOption}&deploymentCode=${action.deploymentCode}&profileModeCode=${action.profileModeCode}&strategies=${action.strategies}`;
    }

    if (action.liveOpticsProjectId) {
      url += `&liveOpticsProjectId=${action.liveOpticsProjectId}`;
    }

    return url;
  }
}
